import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../CartContext';
import { getAllProducts, mediaUrl } from '../api';

export default function SearchResultsPage() {
  const { query } = useParams();
  const navigate  = useNavigate();
  const { addToCart } = useCart();

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [quantities, setQuantities]   = useState({});
  const [added, setAdded]             = useState({});

  const searchTerm = decodeURIComponent(query || '').trim();

  useEffect(() => {
    setLoading(true);
    getAllProducts()
      .then(res => { if (res.success) setAllProducts(res.products || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const products = searchTerm
    ? allProducts.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  const handleQty = (id, val) => {
    const q = Math.max(1, parseInt(val) || 1);
    setQuantities(prev => ({ ...prev, [id]: q }));
  };

  const handleAdd = (product) => {
    const qty = quantities[product.id] || 1;
    addToCart({
      id:       product.id,
      name:     product.name,
      price:    parseFloat(product.price),
      image:    product.image_url,
      category: product.category,
      unit:     product.unit || '',
      isB2B:    false,
    }, qty);
    setAdded(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [product.id]: false })), 1400);
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>

      {/* Breadcrumb */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontWeight: 600, fontSize: '0.8rem', padding: 0 }}>Home</button>
          <span>›</span>
          <span style={{ color: '#111827', fontWeight: 500 }}>Search: "{searchTerm}"</span>
        </div>
      </div>

      <div className="container" style={{ padding: '20px 1rem 3rem' }}>

        {/* Heading */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#111827', margin: 0 }}>
            Results for "{searchTerm}"
          </h1>
          {!loading && (
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{products.length} product{products.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Searching...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* No results */}
        {!loading && products.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🔍</div>
            <p style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>No products found for "{searchTerm}"</p>
            <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '16px' }}>Try a different keyword or browse categories</p>
            <button onClick={() => navigate('/')} style={{ padding: '8px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
              Back to Home
            </button>
          </div>
        )}

        {/* Grid — 5 per row */}
        {!loading && products.length > 0 && (
          <>
            <style>{`
              .km-search-grid {
                display: grid;
                grid-template-columns: repeat(5, 1fr);
                gap: 14px;
              }
              @media (max-width: 1024px) { .km-search-grid { grid-template-columns: repeat(4, 1fr); } }
              @media (max-width: 768px)  { .km-search-grid { grid-template-columns: repeat(3, 1fr); } }
              @media (max-width: 480px)  { .km-search-grid { grid-template-columns: repeat(2, 1fr); } }
            `}</style>
            <div className="km-search-grid">
              {products.map(product => {
                const qty      = quantities[product.id] || 1;
                const price    = parseFloat(product.price);
                const orig     = product.original_price ? parseFloat(product.original_price) : null;
                const discount = orig ? Math.round(((orig - price) / orig) * 100) : null;
                const isAdded  = added[product.id];

                return (
                  <div key={product.id} style={{
                    background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
                    overflow: 'hidden', position: 'relative',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    display: 'flex', flexDirection: 'column',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {isAdded && (
                      <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', background: '#16a34a', color: 'white', borderRadius: '6px', padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', zIndex: 10, boxShadow: '0 2px 8px rgba(22,163,74,0.4)' }}>
                        ✓ Added to cart
                      </div>
                    )}

                    {(product.badge || discount) && (
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: product.badge_color || '#dc2626', color: 'white', padding: '2px 7px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, zIndex: 1 }}>
                        {product.badge || `${discount}% OFF`}
                      </div>
                    )}

                    <div style={{ background: '#f8fafc', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                      {product.image_url
                        ? <img src={mediaUrl(product.image_url)} alt={product.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                        : <span style={{ fontSize: '2.5rem' }}>🛒</span>
                      }
                    </div>

                    <div style={{ padding: '10px 10px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: '2px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {product.name}{product.unit ? ` (${product.unit})` : ''}
                      </p>
                      <p style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '6px' }}>{product.category}</p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#16a34a' }}>₹{price}{product.unit ? `/${product.unit}` : ''}</span>
                        {orig && <span style={{ fontSize: '0.72rem', textDecoration: 'line-through', color: '#9ca3af' }}>₹{orig}</span>}
                      </div>

                      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
                          <button onClick={() => handleQty(product.id, qty - 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>−</button>
                          <span style={{ minWidth: '22px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>{qty}</span>
                          <button onClick={() => handleQty(product.id, qty + 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>+</button>
                        </div>
                        <button
                          onClick={() => handleAdd(product)}
                          style={{ flex: 1, padding: '6px 0', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                          onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
